import { useLayoutEffect, useRef, type ReactNode } from 'react'

import { useMediaQuery } from '../../lib/useMediaQuery'
import { BREAKPOINTS } from '../../lib/breakpoints'
import { Button } from './Button'

/**
 * A list of records, rendered two ways from one definition.
 *
 * The design system has **no table primitive at all** — its only screens are a
 * consumer dashboard and a login mock — so this is net-new, built from the same
 * tokens.
 *
 * Above 768px it is a real `<table>`. Below, the same rows become stacked
 * cards. Two renderings rather than one, deliberately:
 *
 *   - `overflow-x: auto` is the usual answer and it is the wrong one here. It
 *     puts a row's actions off-screen behind a horizontal scroll, on exactly
 *     the device where horizontal scrolling is hardest to discover.
 *   - Re-flowing a `<table>` with CSS (`display: block` on the cells) destroys
 *     the semantics: the row/column relationships go, and a screen reader is
 *     left reading an undifferentiated list of values.
 *
 * So each column declares both a header and a label, and the card rendering
 * uses the label as a `<dt>`. The row data is defined once; only its
 * presentation forks.
 */

export interface Column<T> {
  /** Stable key, also used for React's list key. */
  key: string
  /** Table header. */
  header: ReactNode
  /** Card-view label. Defaults to `header` when it is a plain string. */
  label?: string
  render: (row: T) => ReactNode
  /** Hidden in the card view — for values that only make sense in a grid. */
  tableOnly?: boolean
  /** Right-aligned in the table (counts, dates, actions). */
  align?: 'start' | 'end'
}

/**
 * One period's worth of rows, for the Journal view's week and month headers
 * (Sprint 1.9.11 J2, concept B2).
 *
 * ── WHY `rows` STAYS REQUIRED ON THE COMPONENT ──────────────────────────────
 *
 * The obvious shape is "either `rows` or `groups`", and it is wrong here.
 * `isLoading`, `empty` and the page's single `LoadMore` all branch on how many
 * records the page has LOADED, which is a flat fact about the query and not a
 * fact about any group: a part-loaded page has a last group that is part-loaded
 * too, and a group that happens to be empty is not an empty list. So the caller
 * passes the flat `rows` it already has AND, when it wants headers, the same
 * records partitioned. Nothing here checks that the partition covers `rows` --
 * it is the caller's projection of its own data, and a cross-check would only
 * be a second place to get it wrong.
 */
export interface RowGroup<T> {
  /** Stable key, also used for React's list key. */
  key: string
  /**
   * The group's ACCESSIBLE name ("Week of 28 Sep"). `header` is a `ReactNode`
   * and cannot be read as a string, and the card rendering needs a name for the
   * `<ul>` the way the ungrouped one uses `caption`.
   */
  label: string
  /** Drawn above the group's rows: `GroupHeader`, at both widths. */
  header: ReactNode
  rows: T[]
  /** Collapsed: the header and the footer show, the rows do not (concept B3). */
  collapsed?: boolean
  /**
   * Under the group's rows. The "Show the N days" link when collapsed, and the
   * muted "Showing N of M days" line when the page stopped mid-group -- never a
   * second Load more, which is the cost `History.tsx` has already recorded
   * twice.
   */
  footer?: ReactNode
}

interface DataTableProps<T> {
  caption: string
  columns: Column<T>[]
  rows: T[]
  rowKey: (row: T) => string
  /** Rendered at the end of every row / card. */
  actions?: (row: T) => ReactNode
  /** Shown instead of the table when there is nothing. */
  empty: ReactNode
  isLoading?: boolean
  /**
   * Group the same rows under period headers. **`undefined` is today's path,
   * byte for byte** -- the admin console renders through this component too,
   * and `DataTable.groups.test.tsx` pins its exact markup against a literal.
   */
  groups?: RowGroup<T>[]
}

export function DataTable<T>({
  caption,
  columns,
  rows,
  rowKey,
  actions,
  empty,
  isLoading = false,
  groups,
}: DataTableProps<T>) {
  const isWide = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`)

  /**
   * AN EMPTY `groups` IS NOT "GROUPED WITH NOTHING IN IT".
   *
   * Both branches below used to read `groups ?`, which is true for `[]` — so a
   * caller that passed `groups={[]}` beside a full `rows` got the grouped
   * rendering of zero groups: a `<thead>` with the column headings and not one
   * row under it, or, at phone width, an empty `role="group"` wrapper. The page
   * reported no records while holding records.
   *
   * The component's own contract decides which way it falls back, and it is not
   * the `empty` state: `rows` stays required precisely because `isLoading`,
   * `empty` and the page's `LoadMore` all branch on the flat list BEFORE
   * grouping (see `RowGroup` above), and that branch has already run by here
   * and found rows. `groups` is the caller's PRESENTATION of those same rows,
   * so an empty partition is a caller with no headers to draw — which is
   * exactly the ungrouped path.
   *
   * Written once, here, rather than `groups && groups.length > 0` at each of
   * the two sites: that is how the two renderings came to disagree about
   * `rows.length > 0` in the first place.
   */
  const grouped = groups !== undefined && groups.length > 0

  if (isLoading) {
    return (
      <div className="table-state" role="status">
        <span className="spinner" aria-hidden="true" />
        <span>Loading…</span>
      </div>
    )
  }

  if (!rows.length) {
    /**
     * A NULL `empty` RENDERS NOTHING AT ALL, not an empty bordered panel.
     *
     * History passes null when the read failed: the notice above already says
     * what happened and the range line says what that means for the list, so a
     * third bordered box with nothing in it is noise around an error.
     *
     * The guard has to be HERE and the null has to be computed at the CALL
     * SITE, and the first version of this fix got that wrong: `empty` is a
     * `ReactNode`, so `<EmptyHistory />` is a truthy object whatever the
     * component returns, and a component that returned null still rendered this
     * panel. A guard inside the caller's component could not have fired.
     */
    if (empty === null || empty === undefined) return null
    return <div className="table-state table-state--empty">{empty}</div>
  }

  /**
   * The two row renderings, lifted out of their branches UNCHANGED so the
   * grouped path draws the same card and the same `<tr>` as the flat one. The
   * JSX is byte-identical to what each branch held before `groups` existed;
   * only its address moved.
   */
  const card = (row: T) => (
    <li key={rowKey(row)} className="card card--tight record">
      <dl className="record__fields">
        {columns
          .filter((c) => !c.tableOnly)
          .map((c) => (
            <div className="record__field" key={c.key}>
              <dt className="record__label">{c.label ?? c.header}</dt>
              <dd className="record__value">{c.render(row)}</dd>
            </div>
          ))}
      </dl>
      {actions && <div className="record__actions">{actions(row)}</div>}
    </li>
  )

  if (!isWide) {
    /**
     * Grouped cards (concept C): "Your usual" and each period are a title, a
     * coverage line and the facts, with the day cards under them. One `<ul>`
     * per group rather than one list with heading items, so a screen reader
     * gets "list, 6 items" per period instead of one list whose items are
     * sometimes headings.
     */
    if (grouped) {
      return (
        <div className="record-groups" role="group" aria-label={caption}>
          {groups.map((group) => (
            <div className="record-group" key={group.key}>
              <div className="record-group__head">{group.header}</div>
              {!group.collapsed && group.rows.length > 0 && (
                <ul className="record-list" aria-label={group.label}>
                  {group.rows.map((row) => card(row))}
                </ul>
              )}
              {group.footer !== undefined && group.footer !== null && (
                <div className="record-group__foot">{group.footer}</div>
              )}
            </div>
          ))}
        </div>
      )
    }
    return (
      <ul className="record-list" aria-label={caption}>
        {rows.map((row) => card(row))}
      </ul>
    )
  }

  const bodyRow = (row: T) => (
    <tr key={rowKey(row)}>
      {columns.map((c) => (
        <td key={c.key} className={c.align === 'end' ? 'table__cell--end' : undefined}>
          {c.render(row)}
        </td>
      ))}
      {actions && <td className="table__cell--end table__cell--shrink">{actions(row)}</td>}
    </tr>
  )

  /**
   * A group header and a group footer DO span the table, and the data rows
   * still do not: `columns` is the contract for a record, so a cell that
   * swallowed its neighbours would break the header/cell binding every row
   * depends on. A period's title is not a record, it labels the ones below it,
   * which is what `scope="rowgroup"` says -- "applies to all the remaining
   * cells in the ROW group", and the row group is exactly the `<tbody>` this
   * header opens.
   *
   * It said `colgroup` first, and that was wrong rather than a synonym:
   * `colgroup` means the remaining cells of a COLUMN group, and this table
   * emits no `<colgroup>` element at all. The value therefore named a
   * structure that does not exist, so the one semantic the `<tbody>`-per-period
   * arrangement is here to provide -- "every row under this header belongs to
   * Week of 28 Sep" -- reached a screen reader as nothing.
   */
  const span = columns.length + (actions ? 1 : 0)

  return (
    <div className="table-wrap">
      <table className="table">
        <caption className="visually-hidden">{caption}</caption>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={c.align === 'end' ? 'table__cell--end' : undefined}>
                {c.header}
              </th>
            ))}
            {/* `--shrink` as well as `--end`: an actions column has no data to
                size to, and without it the table algorithm hands it a share of
                the width that the email and status columns then have to wrap
                out of. */}
            {actions && (
              <th scope="col" className="table__cell--end table__cell--shrink">
                <span className="visually-hidden">Actions</span>
              </th>
            )}
          </tr>
        </thead>
        {grouped ? (
          groups.map((group) => (
            // One `<tbody>` per period. Valid HTML, and it is what lets a
            // collapsed group be "a header with no rows" rather than a hole in
            // the middle of one body.
            <tbody key={group.key} className="table__group">
              <tr className="table__group-head">
                <th scope="rowgroup" colSpan={span}>
                  {group.header}
                </th>
              </tr>
              {!group.collapsed && group.rows.map((row) => bodyRow(row))}
              {group.footer !== undefined && group.footer !== null && (
                <tr className="table__group-foot">
                  <td colSpan={span}>{group.footer}</td>
                </tr>
              )}
            </tbody>
          ))
        ) : (
          <tbody>{rows.map((row) => bodyRow(row))}</tbody>
        )}
      </table>
    </div>
  )
}

/**
 * Cursor pagination.
 *
 * "Load more" rather than numbered pages, because a keyset cursor cannot
 * express "page 7" — there is no offset to jump to. Offering page numbers over
 * a cursor API means either lying or silently refetching from the start.
 *
 * §Acceptance 4 is "lists paginate; nothing truncates silently", so the count
 * loaded so far is always stated, and the end of the list says so explicitly
 * rather than simply having no button.
 *
 * ── WHERE FOCUS GOES WHEN THE BUTTON STOPS EXISTING ─────────────────────────
 *
 * Two different losses, and only one of them is `Button`'s to fix.
 *
 * While a page is loading the control is `busy`, which disables it, which blurs
 * it — `Button` now takes that back, so paging from the keyboard keeps the
 * user's place. Measured before the fix on `/history`: press Enter on Load
 * more and 50ms later `document.activeElement` is `<body>`, on a node React
 * never replaced.
 *
 * At the END of the list the button is not disabled, it is REPLACED by a
 * sentence. There is nothing to restore to, so focus has to be moved somewhere
 * deliberate, and the region itself is the honest target: it is the `role=
 * "status"` that now reads "End of the list.", so a keyboard user lands on the
 * answer to the question they just asked rather than at the top of the
 * document.
 */
export function LoadMore({
  loaded,
  nextCursor,
  isLoading,
  onLoadMore,
  noun,
}: {
  loaded: number
  nextCursor: string | null | undefined
  isLoading: boolean
  onLoadMore: () => void
  noun: string
}) {
  const regionRef = useRef<HTMLDivElement>(null)
  const buttonHadFocus = useRef(false)
  const hasMore = !!nextCursor

  useLayoutEffect(() => {
    const hadFocus = buttonHadFocus.current
    // Cleared UNCONDITIONALLY. It used to be cleared only inside the
    // end-of-list branch, so paging once from the keyboard left it true for the
    // life of the component -- and if `hasMore` later went false by any other
    // route (a filter change, a refetch after a row action) while focus had
    // fallen to `<body>`, the region took focus nobody had asked for.
    buttonHadFocus.current = false
    if (hasMore || !hadFocus) return
    // Only if the button's removal is what dropped it. Someone who moved on
    // keeps their place, and `document.activeElement` is never null -- it falls
    // back to `<body>`, which is exactly the signal (trap 42).
    if (document.activeElement === document.body) regionRef.current?.focus()
  }, [hasMore])

  return (
    // `tabIndex={-1}` so the region can be focused deliberately and never
    // tabbed into, which is `.shell__main`'s arrangement for the skip link.
    <div ref={regionRef} className="load-more" role="status" tabIndex={-1}>
      <span className="load-more__count">
        {loaded} {loaded === 1 ? noun : `${noun}s`} loaded
      </span>
      {hasMore ? (
        <Button
          variant="secondary"
          size="sm"
          busy={isLoading}
          onClick={(event) => {
            // A mouse click on WebKit does not focus a button (trap 33), so
            // recording the fact rather than assuming it is what stops this
            // moving focus for someone who never had it.
            buttonHadFocus.current = document.activeElement === event.currentTarget
            onLoadMore()
          }}
        >
          {isLoading ? 'Loading…' : 'Load more'}
        </Button>
      ) : (
        <span className="load-more__end">End of the list.</span>
      )}
    </div>
  )
}
